function renderInline(text) {
  if (!text) return null;
  const parts = String(text).split(/(\*\*[^*]+\*\*|\*[^*]+\*|\[[^\]]+\]\([^)]+\)|https?:\/\/[^\s)]+)/g);
  return parts.map((part, i) => {
    const boldMatch = part.match(/^\*\*([^*]+)\*\*$/);
    if (boldMatch) return <strong key={i}>{boldMatch[1]}</strong>;
    const italicMatch = part.match(/^\*([^*]+)\*$/);
    if (italicMatch) return <em key={i}>{italicMatch[1]}</em>;
    const linkMatch = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (linkMatch) {
      return (
        <a key={i} href={linkMatch[2]} target="_blank" rel="noopener noreferrer">
          {linkMatch[1]}
        </a>
      );
    }
    if (/^https?:\/\//.test(part)) {
      return (
        <a key={i} href={part} target="_blank" rel="noopener noreferrer">
          {part}
        </a>
      );
    }
    return <span key={i}>{part}</span>;
  });
}

function extractYoutubeId(url) {
  const match = String(url || "").match(/(?:v=|\/)([0-9A-Za-z_-]{11}).*/) || String(url || "").match(/youtu\.be\/([0-9A-Za-z_-]{11})/);
  return match ? match[1] : "";
}

export function ContentBlockRenderer({ blocks = [] }) {
  return (
    <div className="article-body">
      {blocks.map((block, i) => {
        switch (block.type) {
          case "heading2":
            return (
              <h2 key={i} className="font-serif text-2xl sm:text-3xl font-bold text-[#10243A] mt-10 mb-4">
                {renderInline(block.text)}
              </h2>
            );
          case "heading3":
            return (
              <h3 key={i} className="font-serif text-xl sm:text-2xl font-semibold text-[#10243A] mt-8 mb-3">
                {renderInline(block.text)}
              </h3>
            );
          case "quote":
            return (
              <blockquote key={i} className="border-l-4 border-[#C47A35] pl-6 my-8 font-serif text-xl sm:text-2xl italic text-[#20252B]">
                {renderInline(block.text)}
              </blockquote>
            );
          case "bullet_list":
            return (
              <ul key={i} className="font-serif list-disc pl-6 space-y-2 my-6 text-base sm:text-lg text-[#20252B]">
                {(block.items || []).map((item, j) => <li key={j}>{renderInline(item)}</li>)}
              </ul>
            );
          case "numbered_list":
            return (
              <ol key={i} className="font-serif list-decimal pl-6 space-y-2 my-6 text-base sm:text-lg text-[#20252B]">
                {(block.items || []).map((item, j) => <li key={j}>{renderInline(item)}</li>)}
              </ol>
            );
          case "sources":
            return (
              <div key={i} className="mt-10 border-t border-[#E4E7E9] pt-6" data-testid="article-sources">
                <p className="text-xs font-extrabold uppercase tracking-wide text-[#66717B] mb-3">Източници</p>
                <ul className="space-y-1.5 text-sm text-[#66717B]">
                  {(block.items || []).map((item, j) => <li key={j}>{renderInline(item)}</li>)}
                </ul>
              </div>
            );
          case "image":
            return (
              <figure key={i} className="my-8 -mx-4 sm:mx-0">
                <img src={block.image_url} alt={block.alt_text || ""} loading="lazy" className="w-full object-cover" />
                {block.caption && <figcaption className="mt-2 text-sm text-[#66717B] text-center">{block.caption}</figcaption>}
              </figure>
            );
          case "youtube_embed": {
            const videoId = extractYoutubeId(block.youtube_url);
            return (
              <div key={i} className="my-8 aspect-video">
                <iframe
                  className="w-full h-full"
                  src={`https://www.youtube.com/embed/${videoId}`}
                  title="YouTube video"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            );
          }
          default:
            return (
              <p key={i} className="font-serif text-base sm:text-lg text-[#20252B] leading-[1.75]">
                {renderInline(block.text)}
              </p>
            );
        }
      })}
    </div>
  );
}
